import { useState } from "react";

import { ProgressBar } from "../../../components/ProgressBar";
import { ReturnSlider } from "../../../components/ReturnSlider";
import {
  DateFormat,
  formatCurrency,
  formatDate,
  predictedDateFromMonths,
  projectInvestmentGoal,
} from "../../../utils";
import {
  valuationAmountText,
  valuationMessageFor,
} from "../../../services/valuation";
import { hasCurrentValuation } from "./investmentGoalStatus";
import { GoalTone, InvestmentGoalCardProps } from "./types";
import {
  Actions,
  AmountRow,
  Card,
  CardTop,
  CurrentAmount,
  GoalName,
  LinkActions,
  MetaRow,
  NameRow,
  SliderRow,
  StatusChip,
  TargetAmount,
} from "./styles";
import Button, { ButtonTypes } from "../../../components/Button";

const RETURN_EPSILON = 0.0001;

export const InvestmentGoalCard = ({
  goal,
  onEdit,
  onArchive,
  onDelete,
  onSaveReturn,
}: InvestmentGoalCardProps) => {
  const [assumedReturn, setAssumedReturn] = useState(goal.assumedAnnualReturn);
  const [isAdjusting, setIsAdjusting] = useState(false);

  const { projection } = goal;
  const periodsPerYear = goal.contributionCadence === "YEARLY" ? 1 : 12;
  const isPriced = hasCurrentValuation(goal);
  const currentAmountText = valuationAmountText({
    value: projection.currentAmount,
    valuation: projection.valuation,
  });

  // Mirrors the API's future-value solver so the slider needs no round-trip.
  const liveProjection = isPriced
    ? projectInvestmentGoal({
        currentValue: projection.currentAmount ?? 0,
        target: goal.targetAmount,
        annualReturn: assumedReturn,
        contribution: goal.contributionRate,
        periodsPerYear,
      })
    : null;
  const livePredictedDate = liveProjection
    ? predictedDateFromMonths(liveProjection.monthsToTarget)
    : null;

  let statusLabel = "Not reachable at this return";
  let statusTone: GoalTone = "warning";
  if (livePredictedDate) {
    statusLabel =
      "Projected " + formatDate(livePredictedDate, DateFormat.MY_SPACE);
    statusTone = "info";
  }
  const valuationMessage = isPriced
    ? null
    : valuationMessageFor(projection.valuation);

  const hasChanged =
    Math.abs(assumedReturn - goal.assumedAnnualReturn) > RETURN_EPSILON;
  const isSliderOpen = isAdjusting && isPriced;

  const closeAdjust = () => {
    setAssumedReturn(goal.assumedAnnualReturn);
    setIsAdjusting(false);
  };

  const handleSaveReturn = () => {
    onSaveReturn(goal, assumedReturn);
    setIsAdjusting(false);
  };

  return (
    <Card data-testid="investment-goal-card">
      <CardTop>
        <NameRow>
          <GoalName>{goal.name}</GoalName>
        </NameRow>
        {isPriced && (
          <StatusChip $tone={statusTone} data-testid="predicted-date">
            {statusLabel}
          </StatusChip>
        )}
      </CardTop>

      <AmountRow>
        <CurrentAmount>{currentAmountText}</CurrentAmount>
        <TargetAmount>of {formatCurrency(goal.targetAmount)}</TargetAmount>
      </AmountRow>

      {isPriced && (
        <ProgressBar
          value={projection.progressPercent ?? 0}
          max={100}
          color="var(--color-primary)"
          minFillPct={2}
          ariaLabel={goal.name + " progress"}
        />
      )}

      {valuationMessage && (
        <MetaRow data-testid="valuation-message">{valuationMessage}</MetaRow>
      )}

      {isSliderOpen && (
        <SliderRow>
          <ReturnSlider value={assumedReturn} onChange={setAssumedReturn} />
        </SliderRow>
      )}

      <Actions>
        <LinkActions>
          <Button
            type={ButtonTypes.link}
            size="small"
            tone="muted"
            onClick={() => onEdit(goal)}
          >
            Edit
          </Button>
          <Button
            type={ButtonTypes.link}
            size="small"
            tone="muted"
            onClick={() => onArchive(goal)}
          >
            Archive
          </Button>
          <Button
            type={ButtonTypes.link}
            size="small"
            tone="danger"
            onClick={() => onDelete(goal)}
          >
            Delete
          </Button>
        </LinkActions>
        {isSliderOpen && (
          <LinkActions>
            <Button
              type={ButtonTypes.link}
              size="small"
              tone="muted"
              onClick={closeAdjust}
            >
              Cancel
            </Button>
            <Button
              type={ButtonTypes.primary}
              size="small"
              disabled={!hasChanged}
              onClick={handleSaveReturn}
            >
              Save return
            </Button>
          </LinkActions>
        )}
        {isPriced && !isAdjusting && (
          <Button
            type={ButtonTypes.link}
            size="small"
            tone="muted"
            onClick={() => setIsAdjusting(true)}
          >
            Adjust return
          </Button>
        )}
      </Actions>
    </Card>
  );
};
